import { getAccessTokenUsingMsal, useMsal } from "@skanska/vsa-client-auth";
import { useEffect, useMemo, useState } from "react";
import { apiConfig } from "../config/AppConfig";
import { ApiEndpoints } from "../models/ApiEndpoints";
import {
  missingImagePlaceholder,
  resolveImageDataOrPlaceholder,
} from "../services/MissingImagePlaceholder";

const imageCache = new Map<string, string>();

export function useEditorImageResolver(
  imageIds: string[]
): Map<string, string> {
  const { instance, inProgress } = useMsal();
  const [resolvedImages, setResolvedImages] = useState<Map<string, string>>(
    () => {
      // Initialize from cache for instant display
      const initial = new Map<string, string>();
      for (const id of imageIds) {
        if (imageCache.has(id)) {
          initial.set(id, imageCache.get(id)!);
        }
      }
      return initial;
    }
  );

  const uniqueImageIds = useMemo(() => [...new Set(imageIds)], [imageIds]);
  const idsKey = uniqueImageIds.join(",");

  useEffect(() => {
    const imageIdsToResolve = idsKey ? idsKey.split(",") : [];
    if (imageIdsToResolve.length === 0) return;

    let cancelled = false;

    async function resolveAll() {
      const newMap = new Map<string, string>();
      const toFetch: string[] = [];

      for (const imageId of imageIdsToResolve) {
        if (imageCache.has(imageId)) {
          newMap.set(imageId, imageCache.get(imageId)!);
        } else {
          toFetch.push(imageId);
        }
      }

      if (toFetch.length > 0) {
        const accessToken = await getAccessTokenUsingMsal(
          inProgress,
          instance,
          apiConfig.loginScopes,
          apiConfig.tenantId
        );

        await Promise.all(
          toFetch.map(async (imageId) => {
            try {
              const endpoint = `${apiConfig.apiUrl}/${ApiEndpoints.ImageCurrent}/${imageId}/content`;
              const response = await fetch(endpoint, {
                headers: {
                  Authorization: `Bearer ${accessToken}`,
                  Accept: "application/json",
                },
              });
              if (!response.ok) {
                imageCache.set(imageId, missingImagePlaceholder);
                newMap.set(imageId, missingImagePlaceholder);
                return;
              }
              const text = await response.text();
              let data: string;
              try {
                data = JSON.parse(text);
              } catch {
                data = text;
              }
              const resolvedImageData =
                await resolveImageDataOrPlaceholder(data);
              imageCache.set(imageId, resolvedImageData);
              newMap.set(imageId, resolvedImageData);
            } catch {
              imageCache.set(imageId, missingImagePlaceholder);
              newMap.set(imageId, missingImagePlaceholder);
            }
          })
        );
      }

      if (!cancelled) {
        setResolvedImages(newMap);
      }
    }

    resolveAll();
    return () => {
      cancelled = true;
    };
  }, [idsKey, inProgress, instance]);

  return resolvedImages;
}
